import { useEffect } from 'react';

import { useAtom } from 'jotai';
import Link from 'next/link';

import { ItemLayout, ItemSideGalleryLayout } from '../store/grpc-free-store';

interface SideGalleryProps extends React.HTMLAttributes<HTMLDivElement> {
  images: string[];
}

const SideGallery = ({ className, images }: SideGalleryProps) => {
  const [layout, setLayout] = useAtom(ItemSideGalleryLayout);

  const toggleLayout = () => {
    setLayout(layout === ItemLayout.Wide ? ItemLayout.Normal : ItemLayout.Wide);
  };

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && layout === ItemLayout.Wide) {
        setLayout(ItemLayout.Normal);
      }
    };

    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [layout, setLayout]);

  return (
    <div
      className={`w-full lg:h-full pt-4 pl-2 md:pl-3 lg:pl-4 pr-2 md:pr-4 ${className} ${
        layout === ItemLayout.Wide
          ? 'lg:w-[calc(3*100vw/5)]'
          : 'lg:w-[calc(2*100vw/5)]'
      } transition-[width] h-full overflow-y-auto flex flex-col relative`}
    >
      {/* Close/Expand Button */}
      <button
        onClick={toggleLayout}
        className="absolute top-9 right-9 z-20 btn btn-circle btn-sm bg-white  hover:bg-white/80 transition-all duration-200"
        style={{ backdropFilter: 'blur(4px)' }}
        aria-label={
          layout === ItemLayout.Wide ? 'Close gallery' : 'Expand gallery'
        }
      >
        {layout === ItemLayout.Wide ? (
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        ) : (
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 7V5a2 2 0 012-2h2M17 3h2a2 2 0 012 2v2M21 17v2a2 2 0 01-2 2h-2M7 21H5a2 2 0 01-2-2v-2"
            />
          </svg>
        )}
      </button>

      {/* Selected / active image */}
      <div
        className={`carousel w-full rounded-md ${
          layout === ItemLayout.Normal ? 'aspect-square' : 'grow'
        }`}
      >
        {images.map((image, index) => {
          const imgSrc = image?.includes('http')
            ? `https://www.tayara.tn/mediaGateway/resize-image?img=${image.substring(
                image.lastIndexOf('/') - 2
              )}&w=1000`
            : `https://www.tayara.tn/mediaGateway/resize-image?img=${image}&w=1000`;
          return (
            <div
              id={`item-caroussel-${index}`}
              key={`item-caroussel-${index}`}
              className="relative carousel-item w-full pt-14 -mt-14 group overflow-hidden"
            >
              <img
                className="absolute top-0 right-0 bottom-0 left-0 w-full h-full scale-110 -z-10 object-cover blur-xl brightness-75"
                src={imgSrc}
                alt="selected image"
              />
              <img
                className={`w-full h-full ${
                  layout === ItemLayout.Normal
                    ? 'object-cover cursor-pointer'
                    : 'object-contain'
                }`}
                src={imgSrc}
                alt="selected image"
                tabIndex={0}
                onFocusCapture={() => {
                  setLayout(ItemLayout.Wide);
                }}
              />
              <div className="opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none flex absolute justify-between transform left-5 right-5 top-1/2">
                {index > 0 ? (
                  <Link
                    href={`#item-caroussel-${index - 1}`}
                    passHref
                    replace
                    className="btn btn-circle btn-sm text-xs bg-white  hover:bg-white/80 pointer-events-auto"
                    style={{ backdropFilter: 'blur(4px)' }}
                  >
                    ❮
                  </Link>
                ) : (
                  <div></div>
                )}
                {index < images.length - 1 ? (
                  <Link
                    href={`#item-caroussel-${index + 1}`}
                    passHref
                    replace
                    className="btn btn-circle btn-sm text-xs bg-white  hover:bg-white/80 pointer-events-auto"
                    style={{ backdropFilter: 'blur(4px)' }}
                  >
                    ❯
                  </Link>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      {/* Image list selector */}
      <div
        className={`flex flex-row content-start gap-2 flex-wrap ${
          layout === ItemLayout.Normal ? '' : 'lg:flex-nowrap'
        } my-2 mx-auto`}
      >
        {images.map((image, index) => {
          const imgSrc = image?.includes('http')
            ? `https://www.tayara.tn/mediaGateway/resize-image?img=${image.substring(
                image.lastIndexOf('/') - 2
              )}&w=1000`
            : `https://www.tayara.tn/mediaGateway/resize-image?img=${image}&w=1000`;
          return (
            <Link
              href={`#item-caroussel-${index}`}
              key={`#item-caroussel-link-${index}`}
              passHref
              replace
              className={`rounded-md cursor-pointer w-16 h-16 overflow-hidden`}
            >
              <img
                className="w-full h-full object-cover"
                src={imgSrc}
                alt={imgSrc}
              />
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default SideGallery;
